import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { clearAssistedLoginSessionToken } from '../../services/assistedLoginSession';
import {
  registerTenantPortalAuthFailureHandler,
  type TenantPortalAuthFailureReason,
} from '../../services/tenantPortalSession';
import { TenantAppearanceDock } from './TenantAppearanceDock';

function SigningOutScreen() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
      <TenantAppearanceDock />
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700 mx-auto mb-4" />
        <p className="text-gray-600 dark:text-gray-400">Your session expired. Signing you out…</p>
      </div>
    </div>
  );
}

/**
 * Registers global handling for tenant-server auth failures: Auth0 sign-out with return to origin (login).
 */
export default function TenantPortalSessionGuard({ children }: { children: React.ReactNode }) {
  const { logout } = useAuth0();
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    const handler = (_reason: TenantPortalAuthFailureReason) => {
      setSigningOut(true);
      if (clearAssistedLoginSessionToken()) {
        window.location.assign('/');
        return;
      }
      logout({ logoutParams: { returnTo: window.location.origin } });
    };
    registerTenantPortalAuthFailureHandler(handler);
    return () => registerTenantPortalAuthFailureHandler(null);
  }, [logout]);

  if (signingOut) {
    return <SigningOutScreen />;
  }

  return <>{children}</>;
}
